import type {
    ClinicianFilter,
    EpisodeOfCareFilter,
    OrderFilter,
    PatientFilter,
    ShiftBlockRecurrenceFilter,
    TagFilter,
    VisitFilter,
    VisitRequestFilter,
} from '@axle-health/graphql-schema';

export type { TagFilter };

/**
 * The root entity a queue counts. Mirrors the backend `RootEntity` values the
 * dashboard uses.
 */
export enum RootEntity {
    CLINICIAN = 'CLINICIAN',
    EPISODE_OF_CARE = 'EPISODE_OF_CARE',
    ORDER = 'ORDER',
    PATIENT = 'PATIENT',
    SHIFT_BLOCK = 'SHIFT_BLOCK',
    VISIT = 'VISIT',
    VISIT_REQUEST = 'VISIT_REQUEST',
}

/** The filter input for each countable entity, keyed by its root entity. */
export type FilterInputByEntity = {
    [RootEntity.CLINICIAN]: ClinicianFilter;
    [RootEntity.EPISODE_OF_CARE]: EpisodeOfCareFilter;
    [RootEntity.ORDER]: OrderFilter;
    [RootEntity.PATIENT]: PatientFilter;
    [RootEntity.SHIFT_BLOCK]: ShiftBlockRecurrenceFilter;
    [RootEntity.VISIT]: VisitFilter;
    [RootEntity.VISIT_REQUEST]: VisitRequestFilter;
};

/**
 * A filter input for one of the countable entities.
 *
 * Typed against `@axle-health/graphql-schema` so the filter shapes follow the
 * schema rather than being hand-maintained, the same way the dashboard's own
 * configs do.
 */
export type FilterInput = FilterInputByEntity[RootEntity];

/** Everything a filter builder is allowed to read. */
export type BuilderContext = {
    /** The instant the filters resolve against. */
    now: Date;
    /** IANA zone the calendar dates in the filters resolve in. */
    timezone: string;
};

/**
 * Produces a queue's filters for a given instant and zone.
 *
 * Builders read dates from `ctx` and never from the ambient clock, so a browser
 * and a container evaluating the same version agree.
 */
export type FilterBuilder<EntityT extends RootEntity = RootEntity> = (
    ctx: BuilderContext,
) => FilterInputByEntity[EntityT];

/**
 * A queue, as both the dashboard and the recorder need it.
 *
 * Presentation belongs to the consumer: row links, empty-table copy, ordering,
 * bulk actions and default-enabled state stay in the dashboard configs and merge
 * onto these by `key`.
 */
export type QueueDefinition<EntityT extends RootEntity = RootEntity> = {
    /** Stable key. Also the `queueKey` a snooze filter matches on. */
    key: string;
    /** Human label, stamped onto each recorded row. */
    label: string;
    rootEntity: EntityT;
    /** Whether the queue splits into active and snoozed counts. */
    snoozable: boolean;
    /**
     * Whether the queue is too expensive to batch with others in one query. The
     * dashboard holds these out of its batched count query, and the recorder does
     * the same.
     */
    heavyQuery: boolean;
    buildFilters: FilterBuilder<EntityT>;
};

/**
 * Every dashboard variation the backend can report for an org.
 *
 * Mirrors the schema's own enum. Kept complete rather than trimmed to what is
 * extracted, so a variation with no definitions is a mapping this package states
 * rather than a name it has never heard of.
 */
export enum DashboardVariation {
    ADVENT_HEALTH = 'ADVENT_HEALTH',
    CARE_OPTIONS_FOR_KIDS = 'CARE_OPTIONS_FOR_KIDS',
    ELEANOR_HEALTH = 'ELEANOR_HEALTH',
    HOME_HEALTH_DEMO = 'HOME_HEALTH_DEMO',
    HOSPICE_DEMO = 'HOSPICE_DEMO',
    JET_HEALTH = 'JET_HEALTH',
    NOBLE_HEALTHCARE = 'NOBLE_HEALTHCARE',
    NORTH_STAR = 'NORTH_STAR',
    NORTH_STAR_UAT = 'NORTH_STAR_UAT',
    PENN_MEDICINE = 'PENN_MEDICINE',
    PINNACLE_HEALTH = 'PINNACLE_HEALTH',
    PINNACLE_HEALTH_UAT = 'PINNACLE_HEALTH_UAT',
    TELADOC = 'TELADOC',
}

const PATIENT_NESTED_TAG_ENTITIES = new Set<RootEntity>([
    RootEntity.EPISODE_OF_CARE,
    RootEntity.ORDER,
    RootEntity.VISIT,
    RootEntity.VISIT_REQUEST,
]);

/** Whether a tag filter reaches the entity through a nested `patient` field. */
export const tagsNestUnderPatient = (rootEntity: RootEntity): boolean =>
    PATIENT_NESTED_TAG_ENTITIES.has(rootEntity);

/** Whether a tag filter can reach the entity at all. */
export const supportsTagFilter = (rootEntity: RootEntity): boolean =>
    rootEntity === RootEntity.PATIENT || tagsNestUnderPatient(rootEntity);
